/**
 * @mks2508/coolify-mks-cli-mcp — SDK, CLI, and MCP server for Coolify.
 *
 * Use as a library (SDK):
 * ```typescript
 * import { Coolify } from '@mks2508/coolify-mks-cli-mcp'
 *
 * const coolify = new Coolify({ url: 'https://coolify.example.com', token: '...' })
 * const apps = await coolify.applications.list()
 * const app = await coolify.applications.resolve('my-app')
 * await coolify.applications.deploy(app.uuid)
 * ```
 *
 * Use as MCP server:
 * ```bash
 * coolify-mcp-server  # stdio transport
 * ```
 *
 * Use as CLI:
 * ```bash
 * coolify-mcp list
 * coolify-mcp deploy
 * coolify-mcp logs
 * ```
 *
 * @module
 */

// ─── SDK: Coolify (primary export) ───────────────────────────────────────────

export { Coolify, type ICoolifyOptions } from "./sdk.js";

// ─── Internal CoolifyService (for MCP/CLI, advanced usage) ──────────────────

export {
  CoolifyService,
  getCoolifyService,
  type ICoolifyEnvVar,
} from "./coolify/index.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export type {
  ICoolifyServer,
  ICoolifyServerResource,
  ICoolifyServerDomain,
  ICoolifyDestination,
  ICoolifyProject,
  ICoolifyTeam,
  ICoolifyApplication,
  ICoolifyDatabase,
  ICoolifyDatabaseBackup,
  ICoolifyService as ICoolifyServiceType,
  ICoolifyPrivateKey,
  ICoolifyDeployment,
  ICoolifyVersion,
  ICoolifyAppOptions,
  ICoolifyAppResult,
  ICoolifyDeployOptions,
  ICoolifyDeployResult,
  ICoolifyDeleteResult,
  ICoolifyUpdateOptions,
  ICoolifyLogsOptions,
  ICoolifyLogs,
  ICoolifyEnvironment,
  IProgressCallback,
  TCoolifyApplicationType,
} from "./coolify/types.js";

// ─── MCP Tools (for building custom MCP servers) ─────────────────────────────

export { coolifyTools } from "./tools/definitions.js";
export { handleToolCall } from "./tools/handlers.js";

// ─── Network diagnostics + operation tracing ─────────────────────────────────

export { inspectNetwork, analyzeDeployFailure } from "./network.js";
export type { INetworkInfo, IDeployFailureAnalysis } from "./network.js";
export { OperationTracer, isMcpMode } from "./trace.js";
export type { ITraceStep, IOperationTrace, TraceCallback } from "./trace.js";

// ─── .coolify.json state (for scaffolder integration) ────────────────────────

export {
  loadCoolifyState,
  resolveUuid,
  type ICoolifyDeployState,
} from "./cli/coolify-state.js";
